import React, { useEffect, useState } from "react"
import { Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton } from "@material-ui/core"
import ClearIcon from '@material-ui/icons/Clear'
import mainStyles from '../WorkerManagementComponent/WorkerManagementComponent.module.css'
import formStyles from '../TeamsManagement/styles.module.css'
import styles from './styles.module.css'
import { BlueBtn, BlueInverseBtn } from "../Styled/StyledButtons"

interface IProps {
    show: boolean
    toggleHandle: () => void
    onSubmit: (phone: string, comment: string, days?: number, permanent?: boolean) => void
}

/**
 * Manual block.
 *
 * Permanent is behind an explicit tick because everything else in this feature expires and
 * has to be re-earned — robocall numbers burn out within a day, so a permanent list only
 * ever accumulates stale entries.
 */
const AddBlock = (props: IProps) => {
    const [phone, setPhone] = useState('')
    const [comment, setComment] = useState('')
    const [days, setDays] = useState<number>(7)
    const [permanent, setPermanent] = useState(false)

    useEffect(() => {
        if (props.show) {
            setPhone('')
            setComment('')
            setDays(7)
            setPermanent(false)
        }
    }, [props.show])

    // Ten significant digits, NANP shape — the same rule the API applies, so an obviously
    // bad number is rejected here instead of round-tripping for a 422.
    const digits = phone.replace(/\D/g, '')
    const normalized = digits.length === 11 && digits.charAt(0) === '1' ? digits.slice(1) : digits
    const phoneValid = /^[2-9][0-9]{2}[2-9][0-9]{6}$/.test(normalized)
    const commentValid = comment.trim().length > 3
    const valid = phoneValid && commentValid

    const submit = () => {
        if (!valid) {
            return
        }
        props.onSubmit('+1' + normalized, comment.trim(), permanent ? undefined : days, permanent)
        props.toggleHandle()
    }

    return (
        <Dialog open={props.show} onClose={props.toggleHandle} maxWidth="sm" fullWidth={true}>
            <DialogTitle>
                Block a number manually
                <IconButton className={mainStyles.floatRight} onClick={props.toggleHandle}>
                    <ClearIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent className={formStyles.popup}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <label className={formStyles.FormLabel}>Phone</label>
                        <input
                            placeholder="+1 805 745 3671"
                            className={`${(!phoneValid && phone !== '') ? formStyles.FormInvalid : ""} ${formStyles.FormControl}`}
                            value={phone}
                            onChange={(event) => setPhone(event.target.value)}
                        />
                        {(!phoneValid && phone !== '') ?
                            <div className={styles.Hint}>
                                Needs to be a US/Canada number. International numbers are not
                                supported by the blocker and would never match.
                            </div>
                            : null}
                    </Grid>
                    <Grid item xs={12}>
                        <label className={formStyles.FormLabel}>Comment (required)</label>
                        <input
                            placeholder="Why is this a robocall?"
                            className={`${(!commentValid && comment !== '') ? formStyles.FormInvalid : ""} ${formStyles.FormControl}`}
                            value={comment}
                            onChange={(event) => setComment(event.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <label className={formStyles.FormLabel}>Days</label>
                        <input
                            type="number"
                            min={1}
                            max={365}
                            disabled={permanent}
                            className={formStyles.FormControl}
                            value={permanent ? '' : days}
                            onChange={(event) => setDays(parseInt(event.target.value, 10) || 1)}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <label className={formStyles.FormLabel}>&nbsp;</label>
                        <label className={styles.Evidence}>
                            <input
                                type="checkbox"
                                checked={permanent}
                                onChange={(event) => setPermanent(event.target.checked)}
                            />
                            &nbsp;Permanent (never expires)
                        </label>
                        <div className={styles.Hint}>
                            Only for numbers you are certain about. Everything else expires so
                            the list stays current.
                        </div>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Grid container spacing={1}>
                    <Grid item xs={6}>
                        <BlueInverseBtn fullWidth={true} onClick={props.toggleHandle}>Cancel</BlueInverseBtn>
                    </Grid>
                    <Grid item xs={6}>
                        <BlueBtn fullWidth={true} disabled={!valid} onClick={submit}>Block</BlueBtn>
                    </Grid>
                </Grid>
            </DialogActions>
        </Dialog>
    )
}

export default AddBlock
